import { subscribeRunProgress } from '../utils/runProgressStream'
// GH #1723 / fs-978.11 + fs-978.12: side-by-side comparison of the production
// match arm against one or more shadow runs, with a one-click admin annotation
// per item.
//
// Nothing here writes scan_results, and nothing here changes what a live scan
// runs on - a triggered run is a shadow run only.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material'
import { useCreate, useGetIdentity, useNotification } from '@refinedev/core'
import { useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@/utils/auth/auth0'

import { dataProvider } from '../providers/dataProvider'
import {
  annotationColour,
  annotationLabel,
  annotationTextColour,
  type AnnotationStatus,
} from '../interfaces/matchRunInterfaces'
import ScanContextNav from './ScanContextNav'
import FundingNeedDialog from './FundingNeedDialog'
import OpportunityDialog from './OpportunityDialog'
import ShadowRunDialog from './ShadowRunDialog'
import ScanDashboardMatchDetails from './ScanDashboardMatchDetails'
import {
  MatchArmCell,
  MatchComparison,
  MatchComparisonItem,
  MatchFeedbackRow,
  MatchStrategy,
  RunProgress,
  MatchRunContext,
  PRODUCTION_ARM,
  PRODUCTION_ARM_FALLBACK_LABEL,
  ProductionCost,
  ScanWithRuns,
  VERDICTS,
} from '../interfaces/matchRunInterfaces'
import { User } from '@/utils/auth/auth0'

const RANK_COLOURS = ['#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3']

const rankColour = (rank: number | null) =>
  rank ? RANK_COLOURS[Math.round(rank) - 1] || '#9e9e9e' : '#e0e0e0'

/**
 * What the chip shows: the exact rank when the repeats agreed, the average when
 * they did not. A strategy that returned 1, 3, 3 reads better as 2.3 than as the
 * modal 3, which hid how far the outlier sat.
 */
const cellRank = (cell: MatchArmCell) => {
  if (cell.stable || cell.mean_rank === null) return cell.rank ?? '-'
  return cell.mean_rank.toFixed(1)
}

const armLabel = (
  arm: string,
  strategies: MatchStrategy[],
  productionLabel = PRODUCTION_ARM_FALLBACK_LABEL
) => {
  if (arm === PRODUCTION_ARM) return productionLabel
  return strategies.find((s) => s.key === arm)?.label || arm
}

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '-' : `$${Number(value).toFixed(4)}`

// Totals run to minutes and hours; raw milliseconds stopped being readable.
const duration = (ms: number | null | undefined) => {
  if (ms === null || ms === undefined) return '-'
  const seconds = Number(ms) / 1000
  if (seconds < 90) return `${seconds.toFixed(1)}s`
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

const millis = (value: number | null | undefined) =>
  value === null || value === undefined
    ? '-'
    : `${Math.round(Number(value)).toLocaleString()}ms`

/** The cell for one arm on one item: rank, the order it implies, and its cost. */
const ArmCell = ({
  cell,
  onRun,
  running,
}: {
  cell: MatchArmCell | undefined
  onRun?: () => void
  running?: boolean
}) => {
  if (!cell) {
    return (
      <TableCell align="center">
        {onRun ? (
          <Tooltip title="Run the default match strategy over this match">
            <span>
              <Button
                size="small"
                startIcon={<RunIcon />}
                onClick={onRun}
                disabled={running}
              >
                {running ? 'running' : 'run'}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            no result
          </Typography>
        )}
      </TableCell>
    )
  }

  if (cell.error) {
    return (
      <TableCell align="center">
        <Tooltip title={cell.error}>
          <Chip label="error" size="small" color="error" />
        </Tooltip>
      </TableCell>
    )
  }

  return (
    <TableCell align="center">
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip
            title={
              cell.repeat_count > 1
                ? cell.stable
                  ? `Same rank on all ${cell.repeat_count} runs`
                  : `Ranged ${cell.rank_low}-${cell.rank_high} across ${cell.repeat_count} runs; showing the average` +
                    (cell.live_rank != null
                      ? `. The customer was served ${cell.live_rank}.`
                      : '')
                : 'One run only'
            }
          >
            <Chip
              label={cellRank(cell)}
              size="small"
              sx={{
                backgroundColor: rankColour(
                  cell.stable ? cell.rank : cell.mean_rank
                ),
                minWidth: 34,
                fontWeight: 600,
                // A dashed edge means the repeats did not agree, so the number
                // is a modal reading rather than a settled one.
                border: cell.stable ? undefined : '2px dashed rgba(0,0,0,0.55)',
              }}
            />
          </Tooltip>
          {cell.repeat_count > 1 && !cell.stable ? (
            <Typography variant="caption" color="warning.main">
              {cell.rank_low}-{cell.rank_high}
            </Typography>
          ) : null}
          {cell.position ? (
            <Tooltip title="Position in the list this arm would present">
              <Typography variant="caption" color="text.secondary">
                #{cell.position}
              </Typography>
            </Tooltip>
          ) : null}
        </Box>
        {cell.score !== null && cell.score !== undefined ? (
          <Typography variant="caption">score {cell.score}</Typography>
        ) : null}
        {cell.certainty ? (
          <Chip
            label={cell.certainty}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: 10 }}
          />
        ) : null}
        {cell.cost_usd !== null || cell.latency_ms !== null ? (
          <Typography variant="caption" color="text.secondary">
            {cell.cost_is_estimated ? '~' : ''}
            {money(cell.cost_usd)} · {millis(cell.latency_ms)}
          </Typography>
        ) : cell.cost_usd_estimated !== undefined ? (
          <Tooltip title="Estimated: the scan's recorded cost shared evenly across its items. Production cost is recorded per scan, not per match.">
            <Typography variant="caption" color="text.secondary">
              ~{money(cell.cost_usd_estimated)}
            </Typography>
          </Tooltip>
        ) : null}
      </Box>
    </TableCell>
  )
}

/** The note already saved against this arm by the current admin, if any. */
/** The feedback row for the subject currently on screen, or undefined. */
const feedbackFor = (
  item: MatchComparisonItem,
  arm: string,
  resultId: number | null | undefined
) =>
  item.feedback.find((row) =>
    arm === PRODUCTION_ARM
      ? row.match_run_result_id === null &&
        row.scan_result_id === item.scan_result_id
      : row.match_run_result_id === resultId
  )

interface AnnotationBarProps {
  item: MatchComparisonItem
  arm: string
  cell: MatchArmCell | undefined
  strategies: MatchStrategy[]
  productionLabel: string
  /** Hands the saved row up so the page can patch it in without a refetch. */
  onSaved: (saved: MatchFeedbackRow) => void
}

/** One-click annotation on one arm of one item, with an optional note. */
const AnnotationBar = ({
  item,
  arm,
  cell,
  strategies,
  productionLabel,
  onSaved,
}: AnnotationBarProps) => {
  const notify = useNotification()
  const user = useGetIdentity().data as User | undefined
  const { mutate: saveFeedback } = useCreate()
  const [note, setNote] = useState('')
  // Whether the box holds unsent typing. A saved note is loaded back below, but
  // reloading it over something half-written would lose the reviewer's words.
  const [noteDirty, setNoteDirty] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  // Which repeat's text is on screen. null means "whichever the API summarised",
  // resolved below so that chip is highlighted from the first render rather than
  // showing text with nothing selected.
  const [shownRepeat, setShownRepeat] = useState<number | null>(null)

  const adminName =
    user?.name ||
    `${user?.given_name || ''} ${user?.family_name || ''}`.trim() ||
    user?.sub

  // The repeat actually on screen: the caller's choice, else the one the API
  // summarised from.
  const activeRepeat =
    shownRepeat ??
    (cell?.repeats?.length ? cell.representative_repeat_index ?? 0 : null)

  // The verdict belongs to the RUN whose text is on screen, not to the strategy's
  // representative. Two different repeats were involved before: the cell's
  // match_run_result_id is the first repeat matching the modal rank, while the chip
  // highlighted on load is the first repeat that has a message. When repeats
  // disagree - "ranked 1-2" - an annotator could read repeat 3, click TOO HIGH, and
  // have it recorded against repeat 1. Clicking through the chips changed the
  // explanation but not the subject being judged.
  const activeResultId =
    (activeRepeat !== null
      ? cell?.repeats?.[activeRepeat]?.match_run_result_id
      : undefined) ?? cell?.match_run_result_id

  const savedNote = feedbackFor(item, arm, activeResultId)?.note ?? null

  // Show the note that was saved. Guarded on `noteDirty` so a reload never
  // overwrites something half-written.
  useEffect(() => {
    if (!noteDirty) setNote(savedNote ?? '')
  }, [savedNote, noteDirty])

  const existing = feedbackFor(item, arm, activeResultId)

  const submit = (verdict: string) => {
    if (!cell) return
    const scanResultId =
      arm === PRODUCTION_ARM ? item.scan_result_id : cell.scan_result_id
    const matchRunResultId = arm === PRODUCTION_ARM ? null : activeResultId ?? null
    const trimmedNote = note.trim() || null

    setSaving(verdict)
    saveFeedback(
      {
        resource: 'match-runs/feedback',
        values: {
          scan_result_id: scanResultId,
          match_run_result_id: matchRunResultId,
          verdict,
          note: trimmedNote,
          admin_name: adminName,
        },
      },
      {
        // refine mutations are not awaitable - patch from onSuccess or the update
        // races the write. Patching one row rather than refetching the comparison:
        // a 185-row reload rebuilt the whole grid, which read as a page refresh and
        // threw away the reviewer's scroll position and open rows.
        onSuccess: (response) => {
          const saved = response?.data as unknown as { id: number } | undefined
          setSaving(null)
          // The note stays on screen: it was saved, and blanking the box read as
          // though it had been thrown away.
          setNoteDirty(false)
          onSaved({
            id: saved?.id ?? -1,
            scan_result_id: scanResultId,
            match_run_result_id: matchRunResultId,
            opportunity_uuid: item.opportunity_uuid,
            verdict,
            note: trimmedNote,
            admin_name: adminName ?? null,
            created_at: new Date().toISOString(),
          })
        },
        onError: () => {
          setSaving(null)
          notify?.open?.({
            type: 'error',
            message: 'Could not save that annotation. Please try again.',
          })
        },
      }
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle2">
        {armLabel(arm, strategies, productionLabel)}
        {existing ? (
          <Chip
            label={`your annotation: ${
              VERDICTS.find((v) => v.value === existing.verdict)?.label ||
              existing.verdict
            }`}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        ) : null}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {(activeRepeat !== null
          ? cell?.repeats?.[activeRepeat]?.message
          : cell?.message) || <em>no explanation recorded</em>}
      </Typography>
      {/* The repeats behind the summarised cell. This is where the r1/r2/r3
          detail belongs - as evidence of stability, not as separate columns. */}
      {/* Shown for a single run too: its score, cost and latency are worth having
          in front of you while you judge the explanation, and a lone chip also
          makes it obvious there is only one reading behind the number. */}
      {cell && cell.repeats && cell.repeats.length ? (
        <Box>
          {cell.repeats.length > 1 ? (
            <Typography variant="caption" color="text.secondary">
              {cell.stable
                ? `Same rank on all ${cell.repeat_count} runs`
                : `Repeats disagreed: ranked ${cell.rank_low}-${cell.rank_high}`}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              One run
            </Typography>
          )}
          <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
            {cell.repeats.map((repeat, index) => {
              const shown = activeRepeat === index
              return (
                <Tooltip
                  key={`${repeat.match_run_id ?? 'live'}-${index}`}
                  title={
                    repeat.run_label ||
                    (repeat.match_run_id
                      ? `run ${repeat.match_run_id}`
                      : 'live scan')
                  }
                >
                  <Chip
                    size="small"
                    // Click to read that run's own explanation; click again to go
                    // back to the representative one. Clearing noteDirty lets the
                    // box reload THIS repeat's saved note - now that the verdict
                    // binds to the displayed run, carrying a half-typed note across
                    // would attach it to a run it was not written about.
                    onClick={() => {
                      setNoteDirty(false)
                      setShownRepeat(index)
                    }}
                    color={shown ? 'primary' : 'default'}
                    variant={shown ? 'filled' : 'outlined'}
                    // Cost and latency only where the run recorded them; the live
                    // production row has neither, so it reads as just its rank.
                    label={[
                      repeat.rank ?? '-',
                      repeat.cost_usd !== null
                        ? `${repeat.cost_is_estimated ? '~' : ''}${money(
                            repeat.cost_usd
                          )}`
                        : null,
                      repeat.latency_ms !== null
                        ? millis(repeat.latency_ms)
                        : null,
                    ]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  />
                </Tooltip>
              )
            })}
          </Box>
        </Box>
      ) : null}
      {/* No verdict form on the baseline. Annotators judge CHALLENGERS against the
          incumbent, so offering the same buttons on the incumbent itself read as a
          third thing to score and was confusing. The explanation text and repeat
          chips above stay: they are the evidence you judge the challengers by, and
          hiding them would leave nothing to compare against. */}
      {arm === PRODUCTION_ARM ? null : (
        <>
          {/* Note above, verdict buttons below: clicking a verdict is what SAVES
              the row, so it reads as the commit step and belongs after the thing
              it commits. */}
          <TextField
            size="small"
            placeholder="note (optional)"
            value={note}
            onChange={(event) => {
              setNoteDirty(true)
              setNote(event.target.value)
            }}
            multiline
            minRows={2}
            fullWidth
          />
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {VERDICTS.map((verdict) => (
              <Button
                key={verdict.value}
                size="small"
                variant={
                  existing?.verdict === verdict.value ? 'contained' : 'outlined'
                }
                disabled={!cell || saving !== null}
                onClick={() => submit(verdict.value)}
              >
                {saving === verdict.value ? '...' : verdict.label}
              </Button>
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}

const ItemRow = ({
  item,
  arms,
  strategies,
  productionLabel,
  onSaved,
  onShowOpportunity,
  onRunArm,
  runningItem,
}: {
  item: MatchComparisonItem
  arms: string[]
  strategies: MatchStrategy[]
  productionLabel: string
  onSaved: (saved: MatchFeedbackRow) => void
  onShowOpportunity: (opportunityUuid: string) => void
  onRunArm: (item: MatchComparisonItem) => void
  runningItem: string | null
}) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          backgroundColor: item.disagrees_with_production
            ? 'rgba(255, 152, 0, 0.08)'
            : undefined,
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell width={40}>
          {open ? <ExpandIcon /> : <CollapseIcon />}
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2">{item.fund_name}</Typography>
            <Tooltip title="Funder criteria, exclusions and profile">
              <IconButton
                size="small"
                onClick={(event) => {
                  // The row toggles on click; this must not also expand it.
                  event.stopPropagation()
                  onShowOpportunity(item.opportunity_uuid)
                }}
              >
                <InfoIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {item.funder_name}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {/* The widest gap between any two individual scores on this item,
              across every run of every strategy including production's replays. */}
          <Tooltip
            title={`Widest gap between any two individual scores on this match, across every run${
              item.disagrees_with_production
                ? ''
                : '. No strategy consistently differs from the baseline.'
            }`}
          >
            <Chip
              // Labelled, not bare: a lone number in a column of rank chips read
              // as another rank.
              label={`spread ${item.rank_spread}`}
              size="small"
              color={
                item.disagrees_with_production
                  ? 'warning'
                  : item.rank_spread
                  ? 'default'
                  : 'success'
              }
              variant={item.disagrees_with_production ? 'filled' : 'outlined'}
            />
          </Tooltip>
          {!item.comparable ? (
            <Tooltip title="Not every match strategy shown produced a result for this opportunity, so its positions are not comparable.">
              <Chip
                label="partial"
                size="small"
                variant="outlined"
                sx={{ ml: 0.5, height: 18, fontSize: 10 }}
              />
            </Tooltip>
          ) : null}
        </TableCell>
        {arms.map((arm) => (
          <ArmCell
            key={arm}
            cell={item.arms[arm]}
            // Only shadow arms can be filled in; production is never re-run.
            onRun={
              arm !== PRODUCTION_ARM && item.scan_result_id !== null
                ? () => onRunArm(item)
                : undefined
            }
            running={runningItem === item.opportunity_uuid}
          />
        ))}
        <TableCell align="center">
          {item.feedback.length ? (
            <Chip label={item.feedback.length} size="small" color="primary" />
          ) : null}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={arms.length + 4} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box py={2} display="flex" flexDirection="column" gap={2}>
              {arms.map((arm) => (
                <React.Fragment key={arm}>
                  <AnnotationBar
                    item={item}
                    arm={arm}
                    cell={item.arms[arm]}
                    strategies={strategies}
                    productionLabel={productionLabel}
                    onSaved={onSaved}
                  />
                  <Divider />
                </React.Fragment>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const StrategySummary = ({
  strategies,
  productionCost,
  productionLabel,
}: {
  strategies: MatchStrategy[]
  productionCost: ProductionCost | null
  productionLabel: string
}) => (
  <TableContainer component={Paper} sx={{ mb: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Match strategy</TableCell>
          <TableCell>Model</TableCell>
          <TableCell>Prompt</TableCell>
          <TableCell align="center">Repeats</TableCell>
          <TableCell align="center">Errors</TableCell>
          <TableCell align="right">Cost</TableCell>
          <TableCell align="right">Model time</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>{productionLabel}</TableCell>
          <TableCell>gpt-4.1</TableCell>
          <TableCell>baseline</TableCell>
          <TableCell align="center">
            <Chip label="live" size="small" />
          </TableCell>
          <TableCell align="center">-</TableCell>
          <TableCell align="right">
            {productionCost ? (
              <Tooltip
                title={
                  productionCost.estimated_from_replays
                    ? "No recorded cost for this scan; priced at the incumbent's own replay rate."
                    : productionCost.by_workflow
                        .map(
                          (row) =>
                            `${row.workflow} (${row.model}): ${money(
                              row.cost_usd
                            )}`
                        )
                        .join('\n')
                }
              >
                <span>
                  {productionCost.estimated_from_replays ? '~' : ''}
                  {money(productionCost.total_usd)}
                  {productionCost.per_item_estimate_usd !== undefined
                    ? ` (~${money(productionCost.per_item_estimate_usd)}/item)`
                    : ''}
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="This scan ran before per-scan AI cost was recorded, so the incumbent's spend is unknown - not zero.">
                <Typography variant="caption" color="text.secondary">
                  not recorded
                </Typography>
              </Tooltip>
            )}
          </TableCell>
          <TableCell align="right">-</TableCell>
        </TableRow>
        {strategies.map((strategy) => (
          <TableRow key={strategy.key}>
            <TableCell>
              {strategy.label}
              {strategy.is_failed ? (
                <Tooltip
                  title={
                    strategy.last_error ||
                    'This run failed. Check the API logs for the cause.'
                  }
                >
                  <Chip
                    size="small"
                    color="error"
                    label={`${strategy.failed_runs} failed`}
                    sx={{ mt: 0.5 }}
                  />
                </Tooltip>
              ) : strategy.is_running && strategy.is_stalled ? (
                <Tooltip title="This run claims to be in progress but has written nothing for a long time - its process almost certainly died. Start it again.">
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label="stalled"
                    sx={{ mt: 0.5 }}
                  />
                </Tooltip>
              ) : strategy.is_running ? (
                <Box sx={{ mt: 0.5, minWidth: 140 }}>
                  <LinearProgress
                    variant={
                      strategy.progress_total ? 'determinate' : 'indeterminate'
                    }
                    value={
                      strategy.progress_total
                        ? (100 * strategy.progress_done) /
                          strategy.progress_total
                        : undefined
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {strategy.progress_done}/{strategy.progress_total} assessments
                    {strategy.progress_done === 0 ? ' · awaiting first result' : ''}
                  </Typography>
                </Box>
              ) : null}
            </TableCell>
            <TableCell>{strategy.model}</TableCell>
            <TableCell>{strategy.prompt_version}</TableCell>
            <TableCell align="center">
              {strategy.is_running ? (
                <Tooltip
                  title={`${strategy.running_runs} unfinished repeat(s), queued or processing. Results join the comparison as each repeat finishes.`}
                >
                  <span>
                    {strategy.run_count ? `${strategy.run_count} runs + ` : ''}
                    {strategy.running_runs} unfinished
                  </span>
                </Tooltip>
              ) : strategy.stability_measured ? (
                `${strategy.run_count} runs`
              ) : (
                <Tooltip title="One run only, so nothing here says whether this strategy is stable.">
                  <span>1 run</span>
                </Tooltip>
              )}
            </TableCell>
            <TableCell align="center">{strategy.error_count}</TableCell>
            {/* Total first, per-run mean beside it: the total is what the strategy
                actually cost, the mean is what one more repeat would add. */}
            <TableCell align="right">
              <Tooltip
                title={`${money(strategy.total_cost_usd)} across ${
                  strategy.run_count
                } run(s); ${money(strategy.mean_cost_per_run_usd)} per run${
                  strategy.cost_is_estimated
                    ? '. Priced from recorded tokens - the provider reported no cost.'
                    : ''
                }`}
              >
                <span>
                  {strategy.cost_is_estimated ? '~' : ''}
                  {money(strategy.total_cost_usd)}{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    ({money(strategy.mean_cost_per_run_usd)})
                  </Typography>
                </span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Summed model time across every call, not wall clock - the runner is concurrent. Per-run mean in brackets.">
                <span>
                  {duration(strategy.total_latency_ms)}{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    ({duration(strategy.mean_latency_per_run_ms)})
                  </Typography>
                </span>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

export default function MatchRunComparison() {
  const notify = useNotification()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getAccessTokenSilently } = useAuth0()

  const needId = searchParams.get('need_id')
  const batchNumber = searchParams.get('batch_number')

  const [scans, setScans] = useState<ScanWithRuns[]>([])
  const [comparison, setComparison] = useState<MatchComparison | null>(null)
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([])
  const [disagreementsOnly, setDisagreementsOnly] = useState(false)
  const [loading, setLoading] = useState(false)
  const [contexts, setContexts] = useState<MatchRunContext[] | null>(null)
  // The matches view is the same dialog the Scan Dashboard opens, rendered here so
  // switching surfaces keeps the scan rather than sending the admin back to pick it.
  const [matchesOpen, setMatchesOpen] = useState(false)
  const [needOpen, setNeedOpen] = useState(false)
  const [opportunityUuid, setOpportunityUuid] = useState<string | null>(null)
  const [runningItem, setRunningItem] = useState<string | null>(null)
  // Sort key is either 'agreement' or an arm key, so production and every variant
  // sort the same way. Default: biggest disagreement first, which is the whole
  // point of the page.
  const [sortBy, setSortBy] = useState<string>('agreement')
  const [sortDesc, setSortDesc] = useState(true)
  const [page, setPage] = useState(0)
  useEffect(() => setPage(0), [needId, batchNumber, disagreementsOnly, sortBy, sortDesc, selectedStrategies])
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null)
  const [launchOpen, setLaunchOpen] = useState(false)
  // Retired strategies are hidden by default. A scan accumulates every variant
  // ever tried, and the dead ends crowd out the comparison that matters.
  const [showRetired, setShowRetired] = useState(false)
  // Annotation progress per scan, keyed `need:batch`. Same source and same colour
  // rule as the Scan Dashboard toggle, so one scan reads the same on both pages.
  const [annotationStatus, setAnnotationStatus] = useState<
    Map<string, AnnotationStatus>
  >(new Map())

  const loadScans = useCallback(async () => {
    const response = await dataProvider.custom!({
      url: '/match-runs/scans',
      method: 'get',
      query: { _start: 0, _end: 200, include_without_runs: true },
    })
    setScans(response.data as unknown as ScanWithRuns[])
  }, [])

  const loadAnnotationStatus = useCallback(async () => {
    const response = await dataProvider.custom!({
      url: '/match-runs/annotation-status',
      method: 'get',
    })
    const rows = response.data as unknown as {
      need_id: number
      batch_number: number
      status: AnnotationStatus
    }[]
    setAnnotationStatus(
      new Map(rows.map((r) => [`${r.need_id}:${r.batch_number}`, r.status]))
    )
  }, [])

  useEffect(() => {
    // Left empty on failure, which paints every scan as "needs a run" rather than
    // inventing progress that was never confirmed.
    loadAnnotationStatus().catch(() => setAnnotationStatus(new Map()))
  }, [loadAnnotationStatus])

  useEffect(() => {
    loadScans().catch(() =>
      notify?.open?.({
        type: 'error',
        message: 'Could not load the list of scans with shadow runs',
      })
    )
  }, [loadScans])

  const comparisonRequest = useRef(0)
  const progressSettled = useRef('')
  const loadComparison = useCallback(async (quiet = false) => {
    const request = ++comparisonRequest.current
    if (!needId || !batchNumber) {
      setComparison(null)
      return
    }
    if (!quiet) setLoading(true)
    try {
      const query: Record<string, string | number> = {
        need_id: Number(needId),
        batch_number: Number(batchNumber),
      }
      if (selectedStrategies.length) {
        query.strategies = selectedStrategies.join(',')
      }
      if (showRetired) {
        query.show_all_strategies = 'true'
      }
      const response = await dataProvider.custom!({
        url: '/match-runs/compare',
        method: 'get',
        query,
      })
      if (request === comparisonRequest.current) setComparison(response.data as unknown as MatchComparison)
    } catch {
      if (request !== comparisonRequest.current) return
      notify?.open?.({
        type: 'error',
        message: 'Could not load the comparison',
      })
    } finally {
      if (request === comparisonRequest.current) setLoading(false)
    }
  }, [needId, batchNumber, selectedStrategies, showRetired])

  useEffect(() => {
    void loadComparison()
    return () => { comparisonRequest.current++ }
  }, [loadComparison])

  const refreshComparison = useRef(loadComparison)
  refreshComparison.current = loadComparison

  // A stalled run is not progressing, so there is nothing to watch.
  const anyRunning = Boolean(
    comparison?.available_strategies.some(
      (strategy) => strategy.is_running && !strategy.is_stalled
    )
  )

  /**
   * Live progress over SSE, the same shape the client-side scans and the
   * proposed-edits list use.
   *
   * This replaced a five-second poll of the whole comparison. That refetched all
   * 185 items and swapped the grid on every tick, which read as the page
   * reloading under you. The stream carries COUNTERS ONLY and patches the
   * strategy rows in place, so the grid is never touched while a run is going.
   *
   * Refresh settled results quietly as each repeat finishes. The counter stream
   * itself never refetches the full comparison on every tick.
   */
  useEffect(() => {
    if (!anyRunning || !needId || !batchNumber) return

    return subscribeRunProgress(async () => {
      const token = await getAccessTokenSilently()
      return `${import.meta.env.VITE_API_URL}/match-runs/progress/stream` +
        `?need_id=${needId}&batch_number=${batchNumber}` +
        `&token=${encodeURIComponent(token)}`
    }, (message) => {
        const payload = message as
          | { done: true }
          | { runs: RunProgress[]; active: number }
        if ('done' in payload || payload.active === 0) {
          // Refresh before clearing is_running can tear down the stream effect.
          void refreshComparison.current(true)
          return false
        }

        // Refresh usable results as each repeat finishes.
        const settled = `${needId}:${batchNumber}:` + payload.runs
          .filter(r => r.status === 'complete' || r.status === 'failed')
          .map(r => `${r.id}:${r.status}`).sort().join(',')
        if (progressSettled.current !== settled) {
          progressSettled.current = settled
          if (payload.runs.some(r => r.status === 'complete' || r.status === 'failed')) {
            void refreshComparison.current(true)
          }
        }

        // Patch only the strategy rows. `items` is deliberately untouched.
        setComparison((current) => {
          if (!current) return current
          const byKey = new Map<string, RunProgress[]>()
          payload.runs.forEach((run) => {
            byKey.set(run.key, [...(byKey.get(run.key) || []), run])
          })

          const merge = (strategy: MatchStrategy): MatchStrategy => {
            const runs = byKey.get(strategy.key)
            if (!runs) return strategy
            const live = runs.filter(
              (r) => r.status === 'running' && !r.stalled
            )
            return {
              ...strategy,
              is_running: live.length > 0,
              running_runs: live.length,
              is_stalled: runs.some((r) => r.status === 'running' && r.stalled),
              progress_done: runs.reduce((sum, r) => sum + r.done, 0),
              progress_total: runs.reduce(
                (sum, r) => sum + (r.items_total || 0),
                0
              ),
            }
          }

          return {
            ...current,
            strategies: current.strategies.map(merge),
            available_strategies: current.available_strategies.map(merge),
          }
        })
        return true
    })

    // `loadComparison` is deliberately not a dependency: it changes identity on
    // every selection change and would tear down a live stream mid-run.
  }, [anyRunning, needId, batchNumber])

  const selectedScan = useMemo(
    () =>
      scans.find(
        (scan) =>
          String(scan.need_id) === needId &&
          String(scan.batch_number) === batchNumber
      ) || null,
    [scans, needId, batchNumber]
  )

  const arms = useMemo(() => {
    if (!comparison) return []
    return [
      PRODUCTION_ARM,
      ...comparison.strategies.map((strategy) => strategy.key),
    ]
  }, [comparison])

  const unselectedStrategies = useMemo(() => {
    if (!comparison) return []
    const shown = new Set(comparison.strategies.map((s) => s.key))
    return comparison.available_strategies.filter((s) => !shown.has(s.key))
  }, [comparison])

  /**
   * Add or remove one strategy from the comparison, then refetch.
   *
   * One control, not two: a separate "hide this column" toggle alongside a
   * multi-select of what to load meant the same question had two answers, and the
   * multi-select had no working deselect.
   */
  const toggleStrategy = (key: string) => {
    if (!comparison) return
    // An empty selection means "the server chose for me", so start from what is
    // actually on screen rather than from an empty list.
    const shown = comparison.strategies.map((s) => s.key)
    setSelectedStrategies(
      shown.includes(key) ? shown.filter((k) => k !== key) : [...shown, key]
    )
  }

  // The summary table shows what is being compared PLUS anything in flight. A
  // strategy whose only runs are still going has no settled data, so it is not a
  // grid column - but it is the thing the admin just launched and wants to watch.
  const summaryStrategies = useMemo(() => {
    if (!comparison) return []
    const shown = new Set(comparison.strategies.map((s) => s.key))
    return [
      ...comparison.strategies,
      ...comparison.available_strategies.filter(
        (s) => (s.is_running || s.is_failed) && !shown.has(s.key)
      ),
    ]
  }, [comparison])

  const productionLabel =
    comparison?.production_arm_label || PRODUCTION_ARM_FALLBACK_LABEL

  const visibleItems = useMemo(() => {
    if (!comparison) return []
    const filtered = disagreementsOnly
      ? comparison.items.filter((item) => item.disagrees_with_production)
      : comparison.items

    const key = (item: MatchComparisonItem) =>
      sortBy === 'agreement'
        ? item.rank_spread
        : // Unranked sorts to the bottom whichever way the column is pointed,
          // rather than pretending a missing result is a rank 0.
          item.arms[sortBy]?.rank ?? null

    return [...filtered].sort((a, b) => {
      const left = key(a)
      const right = key(b)
      if (left === null && right === null) return 0
      if (left === null) return 1
      if (right === null) return -1
      if (left === right)
        return (a.fund_name || '').localeCompare(b.fund_name || '')
      return sortDesc ? right - left : left - right
    })
  }, [comparison?.items, disagreementsOnly, sortBy, sortDesc])

  /**
   * Fold a just-saved annotation into the loaded comparison.
   *
   * One verdict per admin per subject, so an existing row for the same subject and
   * admin is replaced rather than appended - the same rule the two partial unique
   * indexes enforce server-side.
   */
  const applyAnnotation = (saved: MatchFeedbackRow) => {
    setComparison((current) => {
      if (!current) return current
      return {
        ...current,
        items: current.items.map((item) => {
          if (item.opportunity_uuid !== saved.opportunity_uuid) return item
          const sameSubject = (row: MatchFeedbackRow) =>
            row.match_run_result_id === saved.match_run_result_id &&
            row.scan_result_id === saved.scan_result_id &&
            row.admin_name === saved.admin_name
          return {
            ...item,
            feedback: [
              ...item.feedback.filter((row) => !sameSubject(row)),
              saved,
            ],
          }
        }),
      }
    })
    // The picker's "N by you / M total" comes from the scans list, which is loaded
    // once on mount. Without this it kept reading 0 after you had annotated, which
    // looks like the verdict was not saved. Refetched rather than incremented
    // locally: the write is an upsert, so the client cannot tell a new verdict from
    // a changed one and would drift on every correction.
    loadScans().catch(() => undefined)
    loadAnnotationStatus().catch(() => undefined)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDesc(!sortDesc)
    } else {
      setSortBy(key)
      setSortDesc(true)
    }
  }

  const { mutate: triggerRun } = useCreate()
  const itemSubmissions = useRef(new Map<string, string>())

  /** Fill one missing shadow result with the approved variant. */
  const runArmForItem = (item: MatchComparisonItem) => {
    if (!comparison || item.scan_result_id === null) return
    const submissionKey = `${comparison.need_id}:${comparison.batch_number}:${item.scan_result_id}:${comparison.default_strategy.model}:${comparison.default_strategy.prompt}`
    if (!itemSubmissions.current.has(submissionKey)) itemSubmissions.current.set(submissionKey, crypto.randomUUID())
    setRunningItem(item.opportunity_uuid)
    triggerRun(
      {
        resource: 'match-runs/trigger',
        values: {
          need_id: comparison.need_id,
          batch_number: comparison.batch_number,
          model: comparison.default_strategy.model,
          prompt: comparison.default_strategy.prompt,
          scan_result_ids: [item.scan_result_id],
          request_id: itemSubmissions.current.get(submissionKey),
          run_critique: false,
        },
      },
      {
        onSuccess: () => {
          notify?.open?.({
            type: 'success',
            message: `Queued ${comparison.default_strategy.prompt} for ${item.fund_name}`,
            description: 'RunPod workers will process this scan; results appear when ready.',
          })
          itemSubmissions.current.delete(submissionKey)
          setRunningItem(null)
        },
        onError: () => {
          notify?.open?.({ type: 'error', message: 'Could not start that run' })
          setRunningItem(null)
        },
      }
    )
  }

  const openContexts = async () => {
    if (!comparison) return
    const response = await dataProvider.custom!({
      url: '/match-runs/context',
      method: 'get',
      query: {
        runs: comparison.strategies
          .flatMap((strategy) => strategy.run_ids)
          .join(','),
      },
    })
    setContexts(response.data as unknown as MatchRunContext[])
  }

  const exportUrl = `${
    import.meta.env.VITE_API_URL
  }/match-runs/feedback/export?format=csv${needId ? `&need_id=${needId}` : ''}`

  return (
    <Box p={3}>
      <ScanContextNav
        needId={needId ? Number(needId) : null}
        batchNumber={batchNumber ? Number(batchNumber) : null}
        current="compare"
        scanLabel={
          selectedScan
            ? `${selectedScan.org_name} — batch ${selectedScan.batch_number}`
            : undefined
        }
        onShowMatches={() => setMatchesOpen(true)}
        onShowNeed={
          comparison?.funding_need ? () => setNeedOpen(true) : undefined
        }
      />

      <Typography variant="h5" gutterBottom>
        Match engine A/B comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Shadow runs only. Nothing on this page changes what a customer sees.
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ my: 1 }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="scan-select-label">Scan</InputLabel>
            <Select
              labelId="scan-select-label"
              label="Scan"
              value={
                scans.length && needId && batchNumber
                  ? `${needId}:${batchNumber}`
                  : ''
              }
              onChange={(event) => {
                const [need, batch] = String(event.target.value).split(':')
                setSelectedStrategies([])
                setSearchParams({ need_id: need, batch_number: batch })
              }}
            >
              {scans.map((scan) => (
                <MenuItem
                  key={`${scan.need_id}:${scan.batch_number}`}
                  value={`${scan.need_id}:${scan.batch_number}`}
                >
                  {/* run_count is complete stage-1 runs of an ACTIVE strategy, so
                      this number and the Repeats column below count the same thing.
                      It read "44 runs" on a page showing 3 when it counted every
                      match_runs row - stage-2 graders, retired prompts and
                      unfinished runs included. */}
                  {scan.annotation_requested ? (
                    <Chip
                      size="small"
                      // Same status and same colour rule as the Scan Dashboard
                      // toggle: orange needs a run, yellow nothing judged, blue
                      // some, green every 2+ rank divergence judged.
                      label="requested"
                      title={annotationLabel(
                        annotationStatus.get(
                          `${scan.need_id}:${scan.batch_number}`
                        )
                      )}
                      sx={{
                        mr: 1,
                        color: annotationTextColour(
                          annotationStatus.get(
                            `${scan.need_id}:${scan.batch_number}`
                          )
                        ),
                        backgroundColor: annotationColour(
                          annotationStatus.get(
                            `${scan.need_id}:${scan.batch_number}`
                          )
                        ),
                      }}
                    />
                  ) : null}
                  {scan.org_name} — {scan.need_title} (batch {scan.batch_number}
                  , {scan.run_count} completed runs, {scan.my_annotations} by you /{' '}
                  {scan.all_annotations} total)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {comparison && !comparison.same_inputs ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={openContexts}>
              View context
            </Button>
          }
        >
          These match strategies were not given the same inputs — their context
          fingerprints differ, so the ranks are not directly comparable.
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : null}

      {comparison && !loading ? (
        <>
          {!comparison.available_strategies.length ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button size="small" onClick={() => setLaunchOpen(true)}>
                  Run one
                </Button>
              }
            >
              No runs of an active strategy on this scan yet. Older variants are
              hidden — turn on &ldquo;Show retired strategies&rdquo; to see
              them, or run an active one.
            </Alert>
          ) : null}

          <StrategySummary
            strategies={summaryStrategies}
            productionCost={comparison.production_cost}
            productionLabel={productionLabel}
          />

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            <Chip
              label={comparison.strategies.length && comparison.totals.comparable_items ? `${comparison.totals.disagreements} of ${comparison.totals.comparable_items} compared matches disagree with production` : 'Awaiting completed shadow results to compare'}
              color={!comparison.strategies.length || !comparison.totals.comparable_items ? 'default' : comparison.totals.disagreements ? 'warning' : 'success'}
            />
            {comparison.totals.unstable ? (
              <Tooltip title="The challenger's range overlaps the incumbent's on these, so either could have produced the other's answer on a different roll. Not evidence either way.">
                <Chip
                  label={`${comparison.totals.unstable} inconclusive`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            ) : null}
            {comparison.totals.production_unstable ? (
              <Tooltip title="On these, re-running the incumbent on its own production prompt gave a different rank than the live result. Measured across the dev set that happens 42.5% of the time, which is why a challenger only counts as differing when its range misses the incumbent's entirely.">
                <Chip
                  label={`${comparison.totals.production_unstable} baseline disagrees with itself`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            ) : null}
            {comparison.same_inputs ? (
              <Chip label="same inputs" size="small" color="success" />
            ) : null}
            {comparison.totals.comparable_items < comparison.totals.items ? (
              <Tooltip title="Only these opportunities were run by every match strategy shown. Ranks and positions compare over them.">
                <Chip
                  label={`${comparison.totals.comparable_items} comparable`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  checked={disagreementsOnly}
                  onChange={(event) =>
                    setDisagreementsOnly(event.target.checked)
                  }
                />
              }
              label="Disagreements only"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showRetired}
                  onChange={(event) => setShowRetired(event.target.checked)}
                />
              }
              label="Show retired strategies"
            />
            <Button size="small" href={exportUrl}>
              Export annotations (CSV)
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RunIcon />}
              onClick={() => setLaunchOpen(true)}
            >
              Run a shadow scan
            </Button>
          </Box>

          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
            mb={1}
          >
            <Typography variant="caption" color="text.secondary">
              Match strategies:
            </Typography>
            {/* Production is the baseline every disagreement is measured against,
                so it is always in the comparison and is not switchable. */}
            <Chip size="small" label={productionLabel} color="primary" />
            {/* Only what is being compared, each removable by its X. A scan can
                carry 34 strategies; listing them all as chips filled a third of the
                screen, so the rest live behind Add. */}
            {comparison.strategies.map((strategy) => (
              <Chip
                key={strategy.key}
                size="small"
                color="primary"
                label={
                  strategy.stability_measured
                    ? `${strategy.label} ×${strategy.run_count}`
                    : strategy.label
                }
                onDelete={() => toggleStrategy(strategy.key)}
              />
            ))}
            {unselectedStrategies.length ? (
              <>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(event) => setAddAnchor(event.currentTarget)}
                >
                  Add ({unselectedStrategies.length})
                </Button>
                <Menu
                  anchorEl={addAnchor}
                  open={Boolean(addAnchor)}
                  onClose={() => setAddAnchor(null)}
                  slotProps={{ paper: { sx: { maxHeight: 420 } } }}
                >
                  {unselectedStrategies.map((strategy) => (
                    <MenuItem
                      key={strategy.key}
                      onClick={() => {
                        toggleStrategy(strategy.key)
                        setAddAnchor(null)
                      }}
                    >
                      {strategy.label}
                      {strategy.stability_measured
                        ? ` — ${strategy.run_count} runs`
                        : ' — 1 run'}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
          </Box>

          <TablePagination component="div" count={visibleItems.length}
            page={page} rowsPerPage={50} rowsPerPageOptions={[50]}
            onPageChange={(_, next) => setPage(next)} />
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Opportunity</TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      sortBy === 'agreement'
                        ? sortDesc
                          ? 'desc'
                          : 'asc'
                        : false
                    }
                  >
                    <TableSortLabel
                      active={sortBy === 'agreement'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => toggleSort('agreement')}
                    >
                      Agreement
                    </TableSortLabel>
                  </TableCell>
                  {arms.map((arm) => (
                    <TableCell
                      key={arm}
                      align="center"
                      sortDirection={
                        sortBy === arm ? (sortDesc ? 'desc' : 'asc') : false
                      }
                    >
                      <TableSortLabel
                        active={sortBy === arm}
                        direction={sortDesc ? 'desc' : 'asc'}
                        onClick={() => toggleSort(arm)}
                      >
                        {armLabel(arm, comparison.strategies, productionLabel)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center">Annotations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.slice(page * 50, (page + 1) * 50).map((item) => (
                  <ItemRow
                    key={item.opportunity_uuid}
                    item={item}
                    arms={arms}
                    strategies={comparison.strategies}
                    productionLabel={productionLabel}
                    onSaved={applyAnnotation}
                    onShowOpportunity={setOpportunityUuid}
                    onRunArm={runArmForItem}
                    runningItem={runningItem}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={visibleItems.length}
            page={page} rowsPerPage={50} rowsPerPageOptions={[50]}
            onPageChange={(_, next) => setPage(next)} />
        </>
      ) : null}

      <ShadowRunDialog
        open={launchOpen}
        onClose={() => setLaunchOpen(false)}
        scans={scans}
        defaultScan={selectedScan}
        onLaunched={loadComparison}
      />

      <OpportunityDialog
        opportunityUuid={opportunityUuid}
        open={opportunityUuid !== null}
        onClose={() => setOpportunityUuid(null)}
      />

      <FundingNeedDialog
        need={comparison?.funding_need ?? null}
        open={needOpen}
        onClose={() => setNeedOpen(false)}
      />

      {matchesOpen && selectedScan ? (
        <ScanDashboardMatchDetails
          dialogOpen={matchesOpen}
          handleCloseDialog={() => setMatchesOpen(false)}
          selectedScanBatch={selectedScan.batch_number}
          scanDate={selectedScan.scan_date || ''}
          fundingNeedTitle={selectedScan.need_title}
          orgName={selectedScan.org_name}
          fundingNeedId={selectedScan.need_id}
        />
      ) : null}

      <Dialog
        open={contexts !== null}
        onClose={() => setContexts(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Scan-time context per match strategy</DialogTitle>
        <DialogContent>
          {(contexts || []).map((context) => (
            <Box key={context.id} mb={2}>
              <Typography variant="subtitle2">
                {context.label || `run ${context.id}`} —{' '}
                {context.context_fingerprint || 'no fingerprint'}
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 1, maxHeight: 320, overflow: 'auto' }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(context.context_snapshot, null, 2)}
                </pre>
              </Paper>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
